<template lang="pug">
.arrow-menu(
    :class="arrowMenuClass"
  )
    ul.arrow-menu__list(
      ref="list"
      :class="listClass"
    )
      li.arrow-menu__list-item(
        v-for="item in items"
        :key="item.id"
        ref="listItem"
        :class="getItemListClass(item)"
      )
        //- Сделано через component для того чтобы при изменении роута
        //- IntersectionObserver пересоздался
        component.test-pixel-observer(
          v-if="item.testPixel"
          :is="appearedItems.includes(item.id) ? 'div' : 'IntersectionObserver'"
          @appear="addItemToAppearedItems(item.id)"
        )
          img.test-pixel(
            v-if="appearedItems.includes(item.id) && item.testPixel"
            :src="item.testPixel"
            alt="Тестовый пиксель"
          )
        div.arrow-menu__list-item-link-wrapper
          TheLink.arrow-menu__list-item-link(
            v-bind="{href: item.link, name: item.title}"
            @click="onClickMenuItem(item)"
            :style="getItemLinkStyle(item)"
            :class="getItemLinkClass(item)"
          ) {{item.title}}
    .arrow-menu__arrow.arrow-menu__arrow--prev(
      v-if="isVisibleArrows"
      @click="scrollTo('prev')"
    )
      include ../_assets/arrow.svg
    .arrow-menu__arrow.arrow-menu__arrow--next(
      v-if="isVisibleArrows"
      @click="scrollTo('next')"
    )
      include ../_assets/arrow.svg
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import TheLink from '@/components/lh-ui/TheLinkUi/TheLink.vue';
  import IntersectionObserver from '@/components/lh-ui/IntersectionObserver/IntersectionObserver.vue';

  export default defineNuxtComponent({
    name: 'ArrowMenu',
    components: {
      TheLink,
      IntersectionObserver,
    },
    props: {
      items: {
        type: Array as PropType<Array<MenuItem>>,
        required: true,
      },
      theme: {
        type: String,
        default: '',
      },
      scrollItems: {
        type: Number,
        default: 2,
      },
      scrollDuration: {
        type: Number,
        default: 300,
      },
      routePath: {
        type: String,
        required: true,
      },
    },
    data: () => ({
      isVisibleArrows: false,
      windowWidth: 0,
      appearedItems: [] as number[],
    }),
    computed: {
      arrowMenuClass() {
        return this.theme ? `arrow-menu--${this.theme}` : null;
      },
      listClass() {
        return {
          'arrow-menu__list--with-arrow': this.isVisibleArrows,
        };
      },
    },
    watch: {
      items(newValue) {
        if (newValue.length) {
          this.$nextTick(() => {
            this.calcVisibleArrows();
          });
        }
      },
      // При изменении роута тестовые пиксели обнуляются
      routePath(newVal, oldValue): void {
        if (newVal !== oldValue) {
          this.appearedItems = [];
        }
      },
    },
    mounted() {
      this.calcVisibleArrows();
      window.addEventListener('resize', this.calcVisibleArrows);
    },
    unmounted() {
      window.removeEventListener('resize', this.calcVisibleArrows);
    },
    methods: {
      getItemListClass(item: MenuItem) {
        return {
          'arrow-menu__list-item--active': item.isActive,
        };
      },
      getItemLinkStyle(item: MenuItem) {
        const result: Record<string, string> = {};

        if (item.textColor) {
          result[`--menu-item${this.theme ? `-${this.theme}` : ''}-color`] = item.textColor;
        }

        if (item.backgroundColor) {
          result[`--menu-item${this.theme ? `-${this.theme}` : ''}-bg`] = item.backgroundColor;
        }

        return result;
      },
      getItemLinkClass(item: MenuItem) {
        return {
          'arrow-menu__list-item-link--custom': item.backgroundColor,
          'arrow-menu__list-item-link--active': item.isActive,
        };
      },
      onClickMenuItem(item: MenuItem): void {
        this.$emit('click-menu-item', item);
      },
      calcVisibleArrows(): void {
        const list = this.$refs.list as HTMLElement;
        this.isVisibleArrows = list.offsetWidth !== list.scrollWidth;
      },
      scrollTo(direction: 'prev' | 'next'): void {
        this.$emit('scroll-menu', direction);
        const list = this.$refs.list as HTMLElement;
        const listCoords = list.getBoundingClientRect();
        const listItems = this.$refs.listItem as Array<HTMLElement>;

        const invisibleItems = listItems.filter((item: HTMLElement) => {
          const itemCoords = item.getBoundingClientRect();

          return direction === 'prev'
            ? itemCoords.left - listCoords.left < 0
            : listCoords.right < Math.round(itemCoords.right);
        });

        if (!invisibleItems.length) {
          return;
        }

        const targetScrollItem =
          direction === 'prev'
            ? invisibleItems.slice(parseInt(`-${this.scrollItems}`, 10))[0]
            : invisibleItems.slice(0, this.scrollItems).slice(-1)[0];

        const targetScrollLeft =
          direction === 'prev'
            ? targetScrollItem.getBoundingClientRect().left + list.scrollLeft - listCoords.left
            : targetScrollItem.getBoundingClientRect().right - listCoords.right + list.scrollLeft;

        this.animate({
          duration: this.scrollDuration,
          timing(timeFraction: any) {
            return timeFraction;
          },
          draw(progress: any) {
            const diffScrollLeft = Math.round(targetScrollLeft - list.scrollLeft);
            list.scrollLeft += Math.round(progress * diffScrollLeft);
          },
        });
      },
      animate(options: any) {
        const start = performance.now();

        return new Promise<void>((resolve) => {
          requestAnimationFrame(function animate(time) {
            // timeFraction от 0 до 1
            let timeFraction = (time - start) / options.duration;
            if (timeFraction > 1) {
              timeFraction = 1;
            }

            // текущее состояние анимации
            const progress = options.timing(timeFraction);

            options.draw(progress);

            if (timeFraction < 1) {
              requestAnimationFrame(animate);
            } else {
              resolve();
            }
          });
        });
      },
      addItemToAppearedItems(id: number): void {
        if (!this.appearedItems.includes(id)) {
          this.appearedItems.push(id);
        }
      },
    },
  });
</script>

<style lang="scss" src="./ArrowMenu.scss"></style>
